import { Box, Button, Typography } from '@mui/material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { nightOwl } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import UpgradeToPremiumButton from '@/components/UpgradeToPremium/UpgradeToPremiumButton';
import useUserDetails from '@/hooks/useUserDetails';
import { SelectedSymbol } from '@/types/symbols';
import copyToClipboard from '@/utils/copyToClipboard';

import usePowerQueryPreview from './usePowerQueryPreview';

const PowerQueryPreviewScreen = ({
  capturePosthogEvent,
  exportApiUrl,
  symbols
}: {
  capturePosthogEvent: () => void;
  exportApiUrl: string;
  symbols?: Set<SelectedSymbol>;
}) => {
  const { loading, error, fileContent, downloadFile } = usePowerQueryPreview(
    capturePosthogEvent,
    exportApiUrl,
    symbols
  );

  const { isUserBasic, isUserExpired } = useUserDetails();

  if (isUserBasic || isUserExpired)
    return (
      <UpgradeToPremiumButton
        sx={{ ml: 2 }}
        type='upgrade'
      />
    );

  return (
    <Box sx={{ minWidth: 470, maxWidth: 735 }}>
      <Box sx={{ ml: 2, display: 'flex' }}>
        <Button
          disabled={!fileContent || loading}
          sx={{ mr: 2 }}
          variant='contained'
          onClick={() => {
            capturePosthogEvent();
            copyToClipboard(fileContent, 'Code snippet copied to clipboard!');
          }}
        >
          Copy to clipboard
        </Button>
        <Button
          disabled={!fileContent || loading}
          variant='contained'
          onClick={downloadFile}
        >
          Download
        </Button>
      </Box>
      {error && (
        <Typography
          color='error'
          sx={{ p: 2, fontSize: '14px' }}
        >
          An error occurred while fetching data. Please try again.
        </Typography>
      )}
      {loading ? (
        <Typography sx={{ p: 2, fontSize: '14px' }}>
          Retrieving your data, please wait...
        </Typography>
      ) : (
        <Box
          sx={{
            whiteSpace: 'pre',
            fontFamily: 'monospace',
            fontSize: '14px',
            mt: 3
          }}
        >
          <SyntaxHighlighter
            customStyle={{ backgroundColor: '#1A224D', margin: 0, height: 380 }}
            language='powershell'
            style={nightOwl}
          >
            {fileContent}
          </SyntaxHighlighter>
        </Box>
      )}
    </Box>
  );
};

export default PowerQueryPreviewScreen;
